/**
 * CBAM (Carbon Border Adjustment Mechanism) Module
 * Product-Wise Carbon Engine for EU CBAM compliance
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Package, Plus, Edit2, Trash2, Save, X, Check, AlertTriangle,
  Factory, Zap, Flame, TrendingUp, DollarSign, Ship, Globe,
  BarChart3, FileText, Settings, RefreshCw, Lock, Unlock,
  ChevronRight, ChevronDown, Download, Calculator, Leaf,
  Database, ClipboardList, CheckCircle, Clock, ArrowRight, ExternalLink,
  FileSpreadsheet, FileCode
} from 'lucide-react';
import { useAlert } from '../../context/AlertContext';
import { formatDateTime } from '../../utils/dateUtils';

const API = process.env.REACT_APP_BACKEND_URL;

const CBAMModule = ({ study }) => {
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [moduleStatus, setModuleStatus] = useState(null);
  const [products, setProducts] = useState([]);
  const [productions, setProductions] = useState([]);
  const [energyData, setEnergyData] = useState(null);
  const [emissionFactors, setEmissionFactors] = useState([]);
  const [allocation, setAllocation] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [settings, setSettings] = useState({ carbon_price_eur: 80, allocation_method: 'energy', reporting_calendar_year: null, eu_importer_name: '', eu_importer_eori: '', exchange_rate_eur: 90, domestic_carbon_pricing_applicable: false, domestic_carbon_mechanism_name: '', domestic_carbon_price_local: null, domestic_carbon_price_eur: null });
  const [scenarios, setScenarios] = useState([]);
  
  // Phase 2 states
  const [currencyInfo, setCurrencyInfo] = useState(null);
  const [workflowStatus, setWorkflowStatus] = useState(null);
  const [evidence, setEvidence] = useState([]);
  const [batches, setBatches] = useState([]);
  const [renewableOffset, setRenewableOffset] = useState(null);
  
  // Audit Log states
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditSummary, setAuditSummary] = useState(null);
  const [auditFilters, setAuditFilters] = useState({ entity_type: '', user_email: '' });

  // Multi-product enhancement states
  const [productEnergy, setProductEnergy] = useState([]);
  const [coProductionGroups, setCoProductionGroups] = useState([]);
  const [validationIssues, setValidationIssues] = useState([]);
  const [expandedProducts, setExpandedProducts] = useState({});
  const [productPrecursors, setProductPrecursors] = useState({});
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [showPrecursorForm, setShowPrecursorForm] = useState(null);

  // Quarterly Reports + Data Quality states
  const [quarterlyReports, setQuarterlyReports] = useState([]);
  const [dataQuality, setDataQuality] = useState(null);
  
  // Form states
  const [showProductForm, setShowProductForm] = useState(false);
  const [showShipmentForm, setShowShipmentForm] = useState(false);
  const [showBatchForm, setShowBatchForm] = useState(false);
  const [showEvidenceForm, setShowEvidenceForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [allocationMethod, setAllocationMethod] = useState('energy');

  useEffect(() => {
    if (study?.id) {
      fetchAllData();
    }
  }, [study?.id]);

  const fetchAllData = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const config = { headers: { Authorization: `Bearer ${token}` } };

    try {
      const [statusRes, productsRes, settingsRes, dashboardRes, currencyRes, workflowRes, scenariosRes] = await Promise.all([
        axios.get(`${API}/api/cbam/${study.id}/status`, config),
        axios.get(`${API}/api/cbam/${study.id}/products`, config),
        axios.get(`${API}/api/cbam/${study.id}/settings`, config),
        axios.get(`${API}/api/cbam/${study.id}/dashboard`, config),
        axios.get(`${API}/api/cbam/${study.id}/currency-info`, config),
        axios.get(`${API}/api/cbam/${study.id}/workflow-status`, config),
        axios.get(`${API}/api/cbam/${study.id}/settings/scenarios`, config),
      ]);

      setModuleStatus(statusRes.data);
      setProducts(productsRes.data);
      setSettings(settingsRes.data);
      setDashboard(dashboardRes.data);
      setCurrencyInfo(currencyRes.data);
      setWorkflowStatus(workflowRes.data);
      setScenarios(scenariosRes.data || []);
    } catch (error) {
      console.error('Error fetching CBAM data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    const token = localStorage.getItem('token');
    const res = await axios.get(`${API}/api/cbam/${study.id}/products`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setProducts(res.data);
  };

  const fetchProductions = async () => {
    const token = localStorage.getItem('token');
    const res = await axios.get(`${API}/api/cbam/${study.id}/production?period=${selectedPeriod}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setProductions(res.data);
  };

  const fetchEnergyData = async () => {
    const token = localStorage.getItem('token');
    const res = await axios.get(`${API}/api/cbam/${study.id}/energy-data`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setEnergyData(res.data);
  };

  const fetchEmissionFactors = async () => {
    const token = localStorage.getItem('token');
    const res = await axios.get(`${API}/api/cbam/${study.id}/emission-factors`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setEmissionFactors(res.data);
  };

  const fetchAllocation = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(
        `${API}/api/cbam/${study.id}/allocation?period=${selectedPeriod}&method=${allocationMethod}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAllocation(res.data);
    } catch (error) {
      console.error('Error fetching allocation:', error);
    }
  };

  const fetchShipments = async () => {
    const token = localStorage.getItem('token');
    const res = await axios.get(`${API}/api/cbam/${study.id}/shipments`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setShipments(res.data);
  };

  const fetchDashboard = async () => {
    const token = localStorage.getItem('token');
    const res = await axios.get(`${API}/api/cbam/${study.id}/dashboard`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setDashboard(res.data);
  };

  // Phase 2 fetch functions
  const fetchBatches = async () => {
    const token = localStorage.getItem('token');
    const res = await axios.get(`${API}/api/cbam/${study.id}/batches`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setBatches(res.data);
  };

  const fetchEvidence = async () => {
    const token = localStorage.getItem('token');
    const res = await axios.get(`${API}/api/cbam/${study.id}/evidence`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setEvidence(res.data);
  };

  const fetchRenewableOffset = async () => {
    const token = localStorage.getItem('token');
    const res = await axios.get(`${API}/api/cbam/${study.id}/renewable-offset-summary`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setRenewableOffset(res.data);
  };

  const fetchAuditLogs = async () => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams();
    if (auditFilters.entity_type) params.append('entity_type', auditFilters.entity_type);
    if (auditFilters.user_email) params.append('user_email', auditFilters.user_email);
    
    const res = await axios.get(`${API}/api/cbam/${study.id}/audit-log?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setAuditLogs(res.data.logs || res.data);
  };

  const fetchAuditSummary = async () => {
    const token = localStorage.getItem('token');
    const res = await axios.get(`${API}/api/cbam/${study.id}/audit-log/summary`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setAuditSummary(res.data);
  };

  const fetchWorkflow = async () => {
    const token = localStorage.getItem('token');
    const res = await axios.get(`${API}/api/cbam/${study.id}/workflow-status`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setWorkflowStatus(res.data);
  };

  const fetchQuarterlyReports = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/api/cbam/${study.id}/quarterly-reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuarterlyReports(res.data);
    } catch (err) { console.error('Failed to fetch quarterly reports', err); }
  };

  const fetchDataQuality = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/api/cbam/${study.id}/data-quality`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDataQuality(res.data);
    } catch (err) { console.error('Failed to fetch data quality', err); }
  };

  // Multi-product fetch functions
  const fetchProductEnergy = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/api/cbam/${study.id}/product-energy?period=${selectedPeriod}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProductEnergy(res.data);
    } catch (error) { console.error('Error fetching product energy:', error); }
  };

  const fetchCoProductionGroups = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/api/cbam/${study.id}/co-production-groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoProductionGroups(res.data);
    } catch (error) { console.error('Error fetching co-production groups:', error); }
  };

  const fetchValidation = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/api/cbam/${study.id}/validation?period=${selectedPeriod}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setValidationIssues(res.data.issues || []);
    } catch (error) { console.error('Error fetching validation:', error); }
  };

  const fetchPrecursors = async (productId) => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/api/cbam/${study.id}/products/${productId}/precursors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProductPrecursors(prev => ({ ...prev, [productId]: res.data }));
    } catch (error) { console.error('Error fetching precursors:', error); }
  };

  const autofillEnergyFromBatches = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.post(`${API}/api/cbam/${study.id}/product-energy/autofill-from-batches`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess(`Energy splits auto-filled: ${res.data.products_filled} products, ${res.data.total_kwh} kWh`);
      fetchProductEnergy();
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to auto-fill energy from batches');
    }
  };

  const saveProductEnergySplits = async (entries) => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(`${API}/api/cbam/${study.id}/product-energy`, { entries }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess('Energy splits saved');
      fetchProductEnergy();
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to save energy splits');
    }
  };

  const savePrecursor = async (productId, precursorData) => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/api/cbam/${study.id}/products/${productId}/precursors`, precursorData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess('Precursor added');
      fetchPrecursors(productId);
      fetchProducts();
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to add precursor');
    }
  };

  const deletePrecursor = async (precursorId, productId) => {
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/api/cbam/${study.id}/precursors/${precursorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess('Precursor deleted');
      fetchPrecursors(productId);
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to delete precursor');
    }
  };

  const createCoProductionGroup = async (groupData) => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/api/cbam/${study.id}/co-production-groups`, groupData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess('Co-production group created');
      fetchCoProductionGroups();
      fetchProducts();
      setShowGroupForm(false);
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to create co-production group');
    }
  };

  const deleteCoProductionGroup = async (groupId) => {
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/api/cbam/${study.id}/co-production-groups/${groupId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess('Co-production group deleted');
      fetchCoProductionGroups();
      fetchProducts();
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to delete co-production group');
    }
  };

  const toggleProductExpand = (productId) => {
    setExpandedProducts(prev => {
      const newState = { ...prev, [productId]: !prev[productId] };
      if (newState[productId] && !productPrecursors[productId]) {
        fetchPrecursors(productId);
      }
      return newState;
    });
  };

  // Handle tab changes
  useEffect(() => {
    if (!study?.id) return;
    
    if (activeTab === 'products') { fetchProducts(); fetchCoProductionGroups(); }
    else if (activeTab === 'production') { fetchProducts(); fetchProductions(); }
    else if (activeTab === 'energy') { fetchEnergyData(); fetchEmissionFactors(); fetchProducts(); fetchProductEnergy(); }
    else if (activeTab === 'allocation') { fetchProducts(); fetchProductions(); fetchAllocation(); fetchValidation(); }
    else if (activeTab === 'shipments') { fetchProducts(); fetchShipments(); fetchValidation(); }
    else if (activeTab === 'dashboard') fetchDashboard();
    else if (activeTab === 'batches') { fetchProducts(); fetchBatches(); }
    else if (activeTab === 'evidence') { fetchEvidence(); fetchRenewableOffset(); }
    else if (activeTab === 'workflow') fetchWorkflow();
    else if (activeTab === 'quarterly') { fetchQuarterlyReports(); fetchDataQuality(); }
    else if (activeTab === 'audit') { fetchAuditLogs(); fetchAuditSummary(); }
  }, [activeTab, study?.id, selectedPeriod, allocationMethod]);

  // Save product
  const saveProduct = async (productData) => {
    const token = localStorage.getItem('token');
    const config = { headers: { Authorization: `Bearer ${token}` } };

    try {
      if (editingProduct) {
        await axios.put(`${API}/api/cbam/${study.id}/products/${editingProduct.id}`, productData, config);
      } else {
        await axios.post(`${API}/api/cbam/${study.id}/products`, productData, config);
      }
      setShowProductForm(false);
      setEditingProduct(null);
      fetchProducts();
    } catch (error) {
      showError('Error saving product: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Delete product
  const deleteProduct = async (productId) => {
    const confirmed = await confirm('Are you sure you want to delete this product?');
    if (!confirmed) return;

    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/api/cbam/${study.id}/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchProducts();
    } catch (error) {
      showError('Error deleting product');
    }
  };

  // Save production
  const saveProduction = async (productId, qty) => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/api/cbam/${study.id}/production`, {
        period: selectedPeriod,
        product_id: productId,
        production_qty: parseFloat(qty) || 0
      }, { headers: { Authorization: `Bearer ${token}` } });
      showSuccess('Production data saved successfully');
      fetchProductions();
    } catch (error) {
      showError('Error saving production data');
    }
  };

  // Lock allocation
  const lockAllocation = async () => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(
        `${API}/api/cbam/${study.id}/allocation/lock?period=${selectedPeriod}&method=${allocationMethod}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess('Allocation locked successfully!');
      fetchAllocation();
    } catch (error) {
      showError('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Create shipment
  const createShipment = async (shipmentData) => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(
        `${API}/api/cbam/${study.id}/shipments?carbon_price_eur=${settings.carbon_price_eur}`,
        shipmentData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowShipmentForm(false);
      fetchShipments();
      fetchDashboard();
    } catch (error) {
      showError('Error creating shipment: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Save settings
  const saveSettings = async () => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/api/cbam/${study.id}/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess('Settings saved!');
    } catch (error) {
      showError('Error saving settings');
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'products', label: 'Product Master', icon: Package },
    { id: 'production', label: 'Production Data', icon: Factory },
    { id: 'energy', label: 'Energy & Emissions', icon: Zap },
    { id: 'allocation', label: 'Allocation Engine', icon: Calculator },
    { id: 'shipments', label: 'Shipments', icon: Ship },
    { id: 'batches', label: 'Batch Capture', icon: ClipboardList },
    { id: 'evidence', label: 'Evidence Locker', icon: FileText },
    { id: 'workflow', label: 'Approval', icon: CheckCircle },
    { id: 'dashboard', label: 'Cost Exposure', icon: DollarSign },
    { id: 'quarterly', label: 'Quarterly Reports', icon: Globe },
    { id: 'audit', label: 'Audit Log', icon: Database }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
        <span className="ml-3 text-gray-600">Loading CBAM module...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-1 flex items-center gap-2">
              <Leaf className="w-6 h-6" /> CBAM Engine
            </h2>
            <p className="text-emerald-200">Product-Wise Carbon Tracking for EU CBAM Compliance</p>
            <p className="text-sm text-emerald-300 mt-1">{study?.study_name}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-white/20 rounded-lg px-4 py-2 text-center">
              <p className="text-2xl font-bold">€{settings.carbon_price_eur}</p>
              <p className="text-xs text-emerald-200">Carbon Price/tCO₂</p>
            </div>
            {moduleStatus?.ready_for_declaration && (
              <div className="bg-white text-emerald-700 rounded-lg px-4 py-2 flex items-center gap-2 shadow-sm">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">Ready for Declaration</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="flex">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-emerald-600 text-emerald-600 bg-emerald-50'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <OverviewTab status={moduleStatus} settings={settings} onSettingsChange={setSettings} onSaveSettings={saveSettings} studyId={study.id} onSyncComplete={() => { fetchEnergyData(); fetchEmissionFactors(); }} />
          )}

          {/* Products Tab */}
          {activeTab === 'products' && (
            <ProductsTab
              products={products}
              onAdd={() => { setEditingProduct(null); setShowProductForm(true); }}
              onEdit={(p) => { setEditingProduct(p); setShowProductForm(true); }}
              onDelete={deleteProduct}
              expandedProducts={expandedProducts}
              onToggleExpand={toggleProductExpand}
              productPrecursors={productPrecursors}
              onAddPrecursor={(pid) => setShowPrecursorForm(pid)}
              onDeletePrecursor={deletePrecursor}
              coProductionGroups={coProductionGroups}
              onShowGroupForm={() => setShowGroupForm(true)}
              onDeleteGroup={deleteCoProductionGroup}
              showPrecursorForm={showPrecursorForm}
              setShowPrecursorForm={setShowPrecursorForm}
              savePrecursor={savePrecursor}
            />
          )}

          {/* Production Tab */}
          {activeTab === 'production' && (
            <ProductionTab
              products={products}
              productions={productions}
              selectedPeriod={selectedPeriod}
              onPeriodChange={setSelectedPeriod}
              onSave={saveProduction}
            />
          )}

          {/* Energy Tab */}
          {activeTab === 'energy' && (
            <EnergyTab
              energyData={energyData}
              emissionFactors={emissionFactors}
              studyId={study.id}
              onRefresh={() => { fetchEnergyData(); fetchEmissionFactors(); }}
            />
          )}

          {/* Allocation Tab */}
          {activeTab === 'allocation' && (
            <AllocationTab
              allocation={allocation}
              method={allocationMethod}
              onMethodChange={(m) => { setAllocationMethod(m); }}
              selectedPeriod={selectedPeriod}
              onPeriodChange={setSelectedPeriod}
              onLock={lockAllocation}
              onRefresh={fetchAllocation}
              carbonPrice={settings.carbon_price_eur}
              studyId={study.id}
            />
          )}

          {/* Shipments Tab */}
          {activeTab === 'shipments' && (
            <ShipmentsTab
              shipments={shipments}
              products={products}
              carbonPrice={settings.carbon_price_eur}
              onAdd={() => setShowShipmentForm(true)}
            />
          )}

          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && (
            <DashboardTab dashboard={dashboard} scenarios={scenarios} settings={settings} />
          )}

          {/* Phase 2: Batch Capture Tab */}
          {activeTab === 'batches' && (
            <BatchCaptureTab
              batches={batches}
              products={products}
              studyId={study.id}
              onRefresh={fetchBatches}
            />
          )}

          {/* Phase 2: Evidence Locker Tab */}
          {activeTab === 'evidence' && (
            <EvidenceTab
              evidence={evidence}
              renewableOffset={renewableOffset}
              studyId={study.id}
              onRefresh={() => { fetchEvidence(); fetchRenewableOffset(); }}
            />
          )}

          {/* Phase 2: Approval Workflow Tab */}
          {activeTab === 'workflow' && (
            <WorkflowTab
              workflow={workflowStatus}
              currencyInfo={currencyInfo}
              studyId={study.id}
              onRefresh={fetchWorkflow}
            />
          )}

          {/* Quarterly Reports Tab */}
          {activeTab === 'quarterly' && (
            <QuarterlyReportsTab
              reports={quarterlyReports}
              dataQuality={dataQuality}
              studyId={study.id}
              onRefresh={() => { fetchQuarterlyReports(); fetchDataQuality(); }}
            />
          )}

          {/* Audit Log Tab */}
          {activeTab === 'audit' && (
            <AuditLogTab
              logs={auditLogs}
              summary={auditSummary}
              filters={auditFilters}
              onFilterChange={setAuditFilters}
              onRefresh={() => { fetchAuditLogs(); fetchAuditSummary(); }}
            />
          )}
        </div>
      </div>

      {/* Product Form Modal */}
      {showProductForm && (
        <ProductFormModal
          product={editingProduct}
          onSave={saveProduct}
          onClose={() => { setShowProductForm(false); setEditingProduct(null); }}
        />
      )}

      {/* Shipment Form Modal */}
      {showShipmentForm && (
        <ShipmentFormModal
          products={products}
          carbonPrice={settings.carbon_price_eur}
          onSave={createShipment}
          onClose={() => setShowShipmentForm(false)}
        />
      )}

      {/* Co-Production Group Form Modal */}
      {showGroupForm && (
        <CoProductionGroupFormModal
          products={products}
          onSave={createCoProductionGroup}
          onClose={() => setShowGroupForm(false)}
        />
      )}
    </div>
  );
};

// ==================== SUB-COMPONENTS ====================

const OverviewTab = ({ status, settings, onSettingsChange, onSaveSettings, studyId, onSyncComplete }) => {
  const { showSuccess, showError, showWarning } = useAlert();
  const [syncing, setSyncing] = React.useState(false);

  const handleSyncERS = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/cbam/${studyId}/sync-ers-energy`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = res.data;
      const parts = [];
      if (data.products_created > 0) parts.push(`${data.products_created} new products`);
      parts.push(`${data.production_synced} production records`);
      if (data.energy_synced > 0) parts.push(`${data.energy_synced} energy records (${data.total_kwh.toLocaleString()} kWh)`);
      showSuccess(`ERS sync complete: ${parts.join(', ')}`);
      if (data.warnings?.length > 0) data.warnings.forEach(w => showWarning(w));
      if (onSyncComplete) onSyncComplete();
    } catch (error) {
      showError(`Sync failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSyncing(false);
    }
  };

  return (
  <div className="space-y-6">
    {/* Import from ERS */}
    <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-4">
      <div>
        <h4 className="font-semibold text-blue-900 flex items-center gap-2">
          <Database className="w-4 h-4" /> Import from ERS
        </h4>
        <p className="text-sm text-blue-700 mt-1">
          Import products, production quantities, and electricity consumption from ERS Desktop Assessment into CBAM.
        </p>
      </div>
      <button
        onClick={handleSyncERS}
        disabled={syncing}
        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
      >
        <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
        {syncing ? 'Importing...' : 'Import from ERS'}
      </button>
    </div>

    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <StatusCard icon={Package} label="Products Defined" value={status?.products_defined || 0} color="blue" />
      <StatusCard icon={Factory} label="Production Records" value={status?.production_records || 0} color="orange" />
      <StatusCard icon={Zap} label="Energy Records" value={status?.energy_records || 0} color="yellow" />
      <StatusCard icon={Lock} label="Allocations Locked" value={status?.allocations_locked || 0} color="green" />
      <StatusCard icon={Ship} label="Shipments Created" value={status?.shipments_created || 0} color="purple" />
      <StatusCard icon={ClipboardList} label="Batches Captured" value={status?.batches_captured || 0} color="pink" />
    </div>

    <div className="bg-gray-50 rounded-lg p-6">
      <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
        <Settings className="w-5 h-5" /> CBAM Settings
      </h3>
      {/* Row 1: Core settings */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Carbon Price (€/tCO₂)</label>
          <input
            type="number"
            value={settings.carbon_price_eur}
            onChange={(e) => onSettingsChange({ ...settings, carbon_price_eur: parseFloat(e.target.value) || 0 })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Default Allocation Method</label>
          <select
            value={settings.allocation_method}
            onChange={(e) => onSettingsChange({ ...settings, allocation_method: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="energy">Energy-Based</option>
            <option value="mass">Mass-Based</option>
            <option value="process">Process-Specific</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Reporting Year</label>
          <input
            type="number"
            value={settings.reporting_calendar_year || ''}
            onChange={(e) => onSettingsChange({ ...settings, reporting_calendar_year: parseInt(e.target.value) || null })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            placeholder="e.g. 2024"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">INR/EUR Exchange Rate</label>
          <input
            type="number"
            step="0.01"
            value={settings.exchange_rate_eur || 90}
            onChange={(e) => onSettingsChange({ ...settings, exchange_rate_eur: parseFloat(e.target.value) || 90 })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          />
        </div>
      </div>
      {/* Row 2: EU Importer */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">EU Importer Name</label>
          <input
            type="text"
            value={settings.eu_importer_name || ''}
            onChange={(e) => onSettingsChange({ ...settings, eu_importer_name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            placeholder="Enter EU buyer name"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">EU Importer EORI</label>
          <input
            type="text"
            value={settings.eu_importer_eori || ''}
            onChange={(e) => onSettingsChange({ ...settings, eu_importer_eori: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            placeholder="Enter EORI number"
          />
        </div>
        <div className="flex items-end">
          <button
            onClick={onSaveSettings}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 flex items-center gap-2"
          >
            <Save className="w-4 h-4" /> Save Settings
          </button>
        </div>
      </div>
      {/* Row 3: Domestic Carbon Pricing */}
      <div className="border-t pt-4 mt-2">
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Domestic Carbon Price Deduction</h4>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="dom-carbon"
              checked={settings.domestic_carbon_pricing_applicable || false}
              onChange={(e) => onSettingsChange({ ...settings, domestic_carbon_pricing_applicable: e.target.checked })}
              className="w-4 h-4 text-emerald-600 rounded"
            />
            <label htmlFor="dom-carbon" className="text-sm text-gray-700">Domestic carbon pricing applicable</label>
          </div>
          {settings.domestic_carbon_pricing_applicable && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mechanism Name</label>
                <input
                  type="text"
                  value={settings.domestic_carbon_mechanism_name || ''}
                  onChange={(e) => onSettingsChange({ ...settings, domestic_carbon_mechanism_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="e.g. India CCTS"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price (INR/tCO₂e)</label>
                <input
                  type="number"
                  step="0.01"
                  value={settings.domestic_carbon_price_local || ''}
                  onChange={(e) => onSettingsChange({ ...settings, domestic_carbon_price_local: parseFloat(e.target.value) || null })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price (EUR/tCO₂e)</label>
                <input
                  type="number"
                  step="0.01"
                  value={settings.domestic_carbon_price_eur || ''}
                  onChange={(e) => onSettingsChange({ ...settings, domestic_carbon_price_eur: parseFloat(e.target.value) || null })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>

    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6">
      <h3 className="font-bold text-emerald-900 mb-3">CBAM Workflow</h3>
      <div className="flex items-center gap-2 text-sm text-emerald-800 flex-wrap">
        <span className="bg-emerald-100 px-3 py-1 rounded">1. Define Products</span>
        <ArrowRight className="w-4 h-4" />
        <span className="bg-emerald-100 px-3 py-1 rounded">2. Enter Production</span>
        <ArrowRight className="w-4 h-4" />
        <span className="bg-emerald-100 px-3 py-1 rounded">3. Sync Energy</span>
        <ArrowRight className="w-4 h-4" />
        <span className="bg-emerald-100 px-3 py-1 rounded">4. Allocate CO₂</span>
        <ArrowRight className="w-4 h-4" />
        <span className="bg-emerald-100 px-3 py-1 rounded">5. Log Shipments</span>
        <ArrowRight className="w-4 h-4" />
        <span className="bg-emerald-100 px-3 py-1 rounded">6. Export CBAM</span>
      </div>
    </div>
  </div>
  );
};

const StatusCard = ({ icon: Icon, label, value, color }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    orange: 'bg-orange-50 text-orange-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    pink: 'bg-pink-50 text-pink-600'
  };

  return (
    <div className={`${colors[color]} rounded-lg p-4`}>
      <div className="flex items-center gap-3">
        <Icon className="w-8 h-8" />
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-xs opacity-80">{label}</p>
        </div>
      </div>
    </div>
  );
};

const ProductsTab = ({
  products, onAdd, onEdit, onDelete,
  expandedProducts = {}, onToggleExpand = () => {}, productPrecursors = {}, onDeletePrecursor = () => {},
  coProductionGroups = [], onShowGroupForm = () => {}, onDeleteGroup = () => {},
  showPrecursorForm, setShowPrecursorForm = () => {}, savePrecursor = () => {},
}) => {
  const [precursorForm, setPrecursorForm] = useState({
    precursor_name: '', cn_code: '', consumption_rate: '', emission_intensity: '', data_source: 'actual',
    country_of_origin: '', supplier_installation_name: '', verified_by_third_party: false, verifier_name: '',
  });

  const handleSavePrecursor = (productId) => {
    savePrecursor(productId, {
      ...precursorForm,
      consumption_rate: parseFloat(precursorForm.consumption_rate) || 0,
      emission_intensity: parseFloat(precursorForm.emission_intensity) || 0,
    });
    setPrecursorForm({ precursor_name: '', cn_code: '', consumption_rate: '', emission_intensity: '', data_source: 'actual',
      country_of_origin: '', supplier_installation_name: '', verified_by_third_party: false, verifier_name: '' });
    setShowPrecursorForm(null);
  };

  return (
    <div className="space-y-6">
      {/* Co-Production Groups */}
      {coProductionGroups && coProductionGroups.length > 0 && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-bold text-indigo-900 flex items-center gap-2">
              <Factory className="w-4 h-4" /> Co-Production Groups
            </h4>
            <button onClick={onShowGroupForm} className="text-sm px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 flex items-center gap-1">
              <Plus className="w-3 h-3" /> Add Group
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {coProductionGroups.map(group => (
              <div key={group.id} className="bg-white rounded-lg p-3 border border-indigo-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{group.group_name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs px-2 py-1 bg-indigo-100 text-indigo-700 rounded">{group.allocation_method?.replace('_', ' ')}</span>
                    <button onClick={() => onDeleteGroup(group.id)} className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-3 h-3" /></button>
                  </div>
                </div>
                <div className="text-sm text-gray-600">
                  {(group.products || []).map(p => p.product_name).join(', ') || `${group.product_count} products`}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Product Table Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-gray-900">Product Master</h3>
        <div className="flex items-center gap-2">
          {(!coProductionGroups || coProductionGroups.length === 0) && (
            <button onClick={onShowGroupForm} className="px-3 py-2 border border-indigo-300 text-indigo-700 rounded-lg hover:bg-indigo-50 flex items-center gap-1 text-sm">
              <Factory className="w-4 h-4" /> Create Co-Production Group
            </button>
          )}
          <button onClick={onAdd} className="px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 flex items-center gap-2">
            <Plus className="w-4 h-4" /> Add Product
          </button>
        </div>
      </div>

      {products.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Package className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No products defined yet.</p>
          <p className="text-sm text-gray-500">Add products with HS codes for CBAM tracking.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 w-8"></th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Product Name</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">HS Code</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Process Route</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600">CBAM</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {products.map(product => (
                <React.Fragment key={product.id}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <button onClick={() => onToggleExpand(product.id)} className="p-1 text-gray-400 hover:text-gray-600">
                        {expandedProducts[product.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{product.product_name}</span>
                        {product.is_complex_good && <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">Complex Good</span>}
                        {product.co_production_group_id && <span className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full">Co-Prod</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-sm text-gray-600">{product.hs_code}</td>
                    <td className="px-4 py-3 text-gray-600">{product.process_route || '-'}</td>
                    <td className="px-4 py-3 text-center">
                      {product.cbam_applicable ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700"><Check className="w-3 h-3 mr-1" /> Yes</span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">No</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button onClick={() => onEdit(product)} className="p-1 text-blue-600 hover:bg-blue-50 rounded"><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => onDelete(product.id)} className="p-1 text-red-600 hover:bg-red-50 rounded ml-2"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                  {/* Precursors expandable section */}
                  {expandedProducts[product.id] && (
                    <tr>
                      <td colSpan="6" className="px-8 py-3 bg-gray-50">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <h5 className="text-sm font-semibold text-gray-700">Precursor Goods</h5>
                            <button onClick={() => setShowPrecursorForm(showPrecursorForm === product.id ? null : product.id)}
                              className="text-xs px-2 py-1 bg-amber-600 text-white rounded hover:bg-amber-700 flex items-center gap-1">
                              <Plus className="w-3 h-3" /> Add Precursor
                            </button>
                          </div>
                          {product.is_complex_good && (!productPrecursors[product.id] || productPrecursors[product.id].length === 0) && (
                            <div className="text-sm text-amber-600 bg-amber-50 px-3 py-2 rounded flex items-center gap-2">
                              <AlertTriangle className="w-4 h-4" /> No precursors defined for this complex good
                            </div>
                          )}
                          {productPrecursors[product.id] && productPrecursors[product.id].length > 0 && (
                            <table className="w-full text-sm">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Precursor Name</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">CN Code</th>
                                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Rate (per MT)</th>
                                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Intensity (tCO2/MT)</th>
                                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {productPrecursors[product.id].map(prec => (
                                  <tr key={prec.id} className="border-t border-gray-100">
                                    <td className="px-3 py-2 text-gray-800">{prec.precursor_name}</td>
                                    <td className="px-3 py-2 font-mono text-gray-600">{prec.cn_code || '-'}</td>
                                    <td className="px-3 py-2 text-right text-gray-800">{Number(prec.consumption_rate).toFixed(4)}</td>
                                    <td className="px-3 py-2 text-right text-gray-800">{Number(prec.emission_intensity).toFixed(6)}</td>
                                    <td className="px-3 py-2 text-center">
                                      <button onClick={() => onDeletePrecursor(prec.id, product.id)} className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-3 h-3" /></button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                          {showPrecursorForm === product.id && (
                            <div className="bg-white border border-amber-200 rounded-lg p-3 space-y-2">
                              <div className="grid grid-cols-4 gap-2">
                                <input placeholder="Precursor name" value={precursorForm.precursor_name}
                                  onChange={e => setPrecursorForm({...precursorForm, precursor_name: e.target.value})} className="px-2 py-1 border rounded text-sm" />
                                <input placeholder="CN code" value={precursorForm.cn_code}
                                  onChange={e => setPrecursorForm({...precursorForm, cn_code: e.target.value})} className="px-2 py-1 border rounded text-sm" />
                                <input placeholder="Rate per MT" type="number" step="0.0001" value={precursorForm.consumption_rate}
                                  onChange={e => setPrecursorForm({...precursorForm, consumption_rate: e.target.value})} className="px-2 py-1 border rounded text-sm" />
                                <input placeholder="Intensity tCO2/MT" type="number" step="0.000001" value={precursorForm.emission_intensity}
                                  onChange={e => setPrecursorForm({...precursorForm, emission_intensity: e.target.value})} className="px-2 py-1 border rounded text-sm" />
                              </div>
                              <div className="grid grid-cols-4 gap-2">
                                <input placeholder="Country of origin" value={precursorForm.country_of_origin}
                                  onChange={e => setPrecursorForm({...precursorForm, country_of_origin: e.target.value})} className="px-2 py-1 border rounded text-sm" />
                                <input placeholder="Supplier installation" value={precursorForm.supplier_installation_name}
                                  onChange={e => setPrecursorForm({...precursorForm, supplier_installation_name: e.target.value})} className="px-2 py-1 border rounded text-sm" />
                                <div className="flex items-center gap-2">
                                  <input type="checkbox" id={`prec-verified-${product.id}`} checked={precursorForm.verified_by_third_party}
                                    onChange={e => setPrecursorForm({...precursorForm, verified_by_third_party: e.target.checked})} className="w-3 h-3" />
                                  <label htmlFor={`prec-verified-${product.id}`} className="text-xs text-gray-600">3rd party verified</label>
                                </div>
                                {precursorForm.verified_by_third_party && (
                                  <input placeholder="Verifier name" value={precursorForm.verifier_name}
                                    onChange={e => setPrecursorForm({...precursorForm, verifier_name: e.target.value})} className="px-2 py-1 border rounded text-sm" />
                                )}
                              </div>
                              <div className="flex justify-end gap-2">
                                <button onClick={() => setShowPrecursorForm(null)} className="text-xs px-3 py-1 border rounded hover:bg-gray-50">Cancel</button>
                                <button onClick={() => handleSavePrecursor(product.id)} className="text-xs px-3 py-1 bg-amber-600 text-white rounded hover:bg-amber-700">Save</button>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const ProductionTab = ({ products, productions, selectedPeriod, onPeriodChange, onSave }) => {
  const [editValues, setEditValues] = useState({});

  // Reset edit values when period or productions change
  useEffect(() => {
    const vals = {};
    for (const prod of productions) {
      vals[prod.product_id] = prod.production_qty || 0;
    }
    setEditValues(vals);
  }, [selectedPeriod, productions]);

  const getProductionQty = (productId) => {
    if (editValues[productId] !== undefined) return editValues[productId];
    const prod = productions.find(p => p.product_id === productId);
    return prod?.production_qty || '';
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900">Production Data</h3>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Period:</label>
          <input
            type="month"
            value={selectedPeriod}
            onChange={(e) => onPeriodChange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          />
        </div>
      </div>

      {products.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Factory className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No products defined yet.</p>
          <p className="text-sm text-gray-500">Add products first in the Product Master tab.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Product</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">HS Code</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Production (MT)</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {products.filter(p => p.cbam_applicable).map(product => (
                <tr key={product.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{product.product_name}</td>
                  <td className="px-4 py-3 font-mono text-sm text-gray-600">{product.hs_code}</td>
                  <td className="px-4 py-3 text-right">
                    <input
                      type="number"
                      value={getProductionQty(product.id)}
                      onChange={(e) => setEditValues({ ...editValues, [product.id]: e.target.value })}
                      className="w-32 px-3 py-1 border border-gray-300 rounded text-right"
                      placeholder="0"
                    />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => onSave(product.id, editValues[product.id] || getProductionQty(product.id))}
                      className="px-3 py-1 bg-emerald-600 text-white rounded text-sm hover:bg-emerald-700"
                    >
                      Save
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const EnergyTab = ({ energyData, emissionFactors, studyId, onRefresh }) => {
  const { showSuccess, showError, showWarning } = useAlert();
  const [syncing, setSyncing] = React.useState(false);

  const handleSyncERS = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/cbam/${studyId}/sync-ers-energy`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = res.data;
      const parts = [];
      if (data.products_created > 0) parts.push(`${data.products_created} new products`);
      parts.push(`${data.production_synced} production records`);
      if (data.energy_synced > 0) parts.push(`${data.energy_synced} energy records (${data.total_kwh.toLocaleString()} kWh)`);
      showSuccess(`ERS sync complete: ${parts.join(', ')}`);
      if (data.warnings?.length > 0) {
        data.warnings.forEach(w => showWarning(w));
      }
      if (onRefresh) onRefresh();
    } catch (error) {
      const detail = error.response?.data?.detail || error.message;
      showError(`Sync failed: ${detail}`);
    } finally {
      setSyncing(false);
    }
  };

  if (!energyData) return <div className="text-center py-8">Loading energy data...</div>;

  const emissions = energyData.emissions || {};

  return (
    <div className="space-y-6">
      {/* Import from ERS */}
      <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div>
          <h4 className="font-semibold text-blue-900 flex items-center gap-2">
            <Database className="w-4 h-4" /> Product Energy from ERS
          </h4>
          <p className="text-sm text-blue-700 mt-1">
            Import product-level electricity consumption from ERS Desktop Assessment into CBAM for energy-based allocation.
          </p>
        </div>
        <button
          onClick={handleSyncERS}
          disabled={syncing}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
        >
          <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Importing...' : 'Import from ERS'}
        </button>
      </div>

      {/* Emissions Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-orange-50 rounded-lg p-4">
          <div className="flex items-center gap-2 text-orange-600 mb-2">
            <Flame className="w-5 h-5" />
            <span className="font-semibold">Scope 1</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{emissions.scope1_tco2?.toFixed(2) || 0}</p>
          <p className="text-xs text-gray-500">tCO₂ (Direct)</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-center gap-2 text-blue-600 mb-2">
            <Zap className="w-5 h-5" />
            <span className="font-semibold">Scope 2</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{emissions.scope2_tco2?.toFixed(2) || 0}</p>
          <p className="text-xs text-gray-500">tCO₂ (Electricity)</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex items-center gap-2 text-green-600 mb-2">
            <Leaf className="w-5 h-5" />
            <span className="font-semibold">Renewable Offset</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{emissions.renewable_offset_tco2?.toFixed(2) || 0}</p>
          <p className="text-xs text-gray-500">tCO₂</p>
        </div>
        <div className="bg-gray-100 rounded-lg p-4">
          <div className="flex items-center gap-2 text-gray-700 mb-2">
            <Globe className="w-5 h-5" />
            <span className="font-semibold">Total CBAM</span>
          </div>
          <p className="text-2xl font-bold text-emerald-600">{emissions.total_tco2?.toFixed(2) || 0}</p>
          <p className="text-xs text-gray-500">tCO₂e</p>
        </div>
      </div>

      {/* Electricity Data */}
      <div>
        <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Zap className="w-4 h-4 text-yellow-500" /> Electricity Data (from ERS)
        </h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left">Period</th>
                <th className="px-3 py-2 text-right">kWh</th>
                <th className="px-3 py-2 text-right">Bill (₹)</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {(energyData.electricity || []).slice(0, 12).map((e, i) => (
                <tr key={i}>
                  <td className="px-3 py-2">{e.month}</td>
                  <td className="px-3 py-2 text-right">{(e.consumed_units_kwh || 0).toLocaleString()}</td>
                  <td className="px-3 py-2 text-right">{(e.bill_amount || 0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Fuel Data */}
      <div>
        <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Flame className="w-4 h-4 text-orange-500" /> Fuel Data (from ERS)
        </h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left">Period</th>
                <th className="px-3 py-2 text-left">Fuel Type</th>
                <th className="px-3 py-2 text-right">Quantity</th>
                <th className="px-3 py-2 text-left">Unit</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {(energyData.fuels || []).slice(0, 12).map((f, i) => (
                <tr key={i}>
                  <td className="px-3 py-2">{f.month}</td>
                  <td className="px-3 py-2">{f.fuel_type}</td>
                  <td className="px-3 py-2 text-right">{(f.quantity || 0).toLocaleString()}</td>
                  <td className="px-3 py-2">{f.unit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Emission Factors */}
      <div>
        <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Database className="w-4 h-4 text-indigo-500" /> Emission Factors
        </h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left">Fuel Type</th>
                <th className="px-3 py-2 text-left">Unit</th>
                <th className="px-3 py-2 text-right">EF (tCO₂/unit)</th>
                <th className="px-3 py-2 text-left">Source</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {emissionFactors.map((ef, i) => (
                <tr key={i}>
                  <td className="px-3 py-2 font-medium">{ef.fuel_type}</td>
                  <td className="px-3 py-2">{ef.unit}</td>
                  <td className="px-3 py-2 text-right font-mono">{ef.emission_factor}</td>
                  <td className="px-3 py-2 text-gray-500">{ef.source || ef.version}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const AllocationTab = ({ allocation, method, onMethodChange, selectedPeriod, onPeriodChange, onLock, onRefresh, carbonPrice, studyId }) => {
  const { showSuccess, showError, showWarning } = useAlert();
  const [syncing, setSyncing] = React.useState(false);

  const handleSyncERS = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/cbam/${studyId}/sync-ers-energy`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess(res.data.message);
      if (res.data.warnings?.length > 0) res.data.warnings.forEach(w => showWarning(w));
      if (onRefresh) onRefresh();
    } catch (error) {
      showError(error.response?.data?.detail || error.message);
    } finally {
      setSyncing(false);
    }
  };

  return (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <h3 className="text-lg font-bold text-gray-900">Carbon Allocation Engine</h3>
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Period:</label>
          <input
            type="month"
            value={selectedPeriod}
            onChange={(e) => onPeriodChange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Method:</label>
          <select
            value={method}
            onChange={(e) => onMethodChange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="energy">Energy-Based</option>
            <option value="mass">Mass-Based</option>
            <option value="process">Process-Specific</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Granularity:</label>
          <select className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
            <option value="batch">Batch-level</option>
            <option value="equipment">Equipment-level</option>
            <option value="product">Product-level</option>
            <option value="plant">Plant-level</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Metering:</label>
          <select className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
            <option value="direct">Direct</option>
            <option value="proxy">Proxy/Benchmark</option>
            <option value="none">None</option>
          </select>
        </div>
        <button
          onClick={handleSyncERS}
          disabled={syncing}
          className="flex items-center gap-1 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg border border-blue-200"
          title="Import product energy from ERS"
        >
          <Database className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Syncing...' : 'Sync ERS'}
        </button>
        <button onClick={onRefresh} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
    </div>

    {allocation && (
      <>
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="bg-gray-100 rounded-lg p-4">
            <p className="text-sm text-gray-600">Total Plant CO₂</p>
            <p className="text-2xl font-bold text-gray-900">{allocation.total_plant_co2_tco2?.toFixed(3)} t</p>
          </div>
          <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
            <p className="text-sm text-gray-600">Scope 1 (Direct)</p>
            <p className="text-2xl font-bold text-orange-700">{allocation.scope1_tco2?.toFixed(3)} t</p>
            <p className="text-xs text-gray-500 mt-1">{allocation.total_plant_co2_tco2 > 0 ? ((allocation.scope1_tco2 / allocation.total_plant_co2_tco2) * 100).toFixed(1) : 0}% of total</p>
          </div>
          <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
            <p className="text-sm text-gray-600">Scope 2 (Indirect)</p>
            <p className="text-2xl font-bold text-indigo-700">{allocation.scope2_tco2?.toFixed(3)} t</p>
            <p className="text-xs text-gray-500 mt-1">{allocation.total_plant_co2_tco2 > 0 ? ((allocation.scope2_tco2 / allocation.total_plant_co2_tco2) * 100).toFixed(1) : 0}% of total</p>
          </div>
          <div className="bg-emerald-50 rounded-lg p-4">
            <p className="text-sm text-gray-600">Allocated Total</p>
            <p className="text-2xl font-bold text-emerald-600">{allocation.allocated_total_tco2?.toFixed(3)} t</p>
          </div>
          <div className={`rounded-lg p-4 ${allocation.allocation_valid ? 'bg-green-50' : 'bg-red-50'}`}>
            <p className="text-sm text-gray-600">Tolerance</p>
            <p className={`text-2xl font-bold ${allocation.allocation_valid ? 'text-green-600' : 'text-red-600'}`}>
              {allocation.tolerance_percent}%
            </p>
          </div>
          <div className="flex items-center justify-center">
            {allocation.is_locked ? (
              <div className="px-4 py-2 rounded-lg font-medium flex items-center gap-2 bg-purple-100 text-purple-700 border border-purple-300">
                <Lock className="w-4 h-4" /> Locked
              </div>
            ) : (
              <button
                onClick={onLock}
                disabled={!allocation.allocation_valid}
                className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${
                  allocation.allocation_valid
                    ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                <Unlock className="w-4 h-4" /> Lock Allocation
              </button>
            )}
          </div>
        </div>

        {/* Validation */}
        {!allocation.allocation_valid && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <div>
              <p className="font-medium text-red-800">Allocation Mismatch</p>
              <p className="text-sm text-red-600">
                Difference of {allocation.tolerance_percent}% exceeds 1% tolerance. Cannot lock allocation.
              </p>
            </div>
          </div>
        )}

        {/* CBAM v2: Confidence Level Info */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <p className="font-semibold text-blue-900 mb-2">CBAM v2: Confidence Levels</p>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">🟢 High</span>
                  <span className="text-blue-800">Process-specific allocation</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">🟡 Medium</span>
                  <span className="text-blue-800">Energy-based allocation</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">🔴 Low</span>
                  <span className="text-blue-800">Mass-based allocation</span>
                </div>
              </div>
              <p className="text-xs text-blue-700 mt-2">
                Confidence reflects data and allocation strength, not regulatory validity. Higher confidence increases EU importer trust.
              </p>
            </div>
          </div>
        </div>

        {/* Allocations Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold">Product</th>
                {method === 'energy' && <th className="px-4 py-3 text-right text-sm font-semibold">Energy (kWh)</th>}
                <th className="px-4 py-3 text-right text-sm font-semibold">{method === 'energy' ? 'Energy Share' : 'Mass Share'}</th>
                <th className="px-4 py-3 text-right text-sm font-semibold">Production (MT)</th>
                <th className="px-4 py-3 text-right text-sm font-semibold">Allocated CO₂ (t)</th>
                <th className="px-4 py-3 text-right text-sm font-semibold" style={{color: '#c2410c'}}>Scope 1 (t)</th>
                <th className="px-4 py-3 text-right text-sm font-semibold" style={{color: '#4338ca'}}>Scope 2 (t)</th>
                <th className="px-4 py-3 text-right text-sm font-semibold">Intensity (tCO₂/MT)</th>
                <th className="px-4 py-3 text-right text-sm font-semibold">Est. CBAM Cost/MT</th>
                <th className="px-4 py-3 text-center text-sm font-semibold">Confidence</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {(allocation.allocations || []).map((alloc, i) => {
                const estCostPerMt = (alloc.intensity_tco2_per_mt || 0) * (carbonPrice || 80);
                return (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{alloc.product_name}</td>
                  {method === 'energy' && <td className="px-4 py-3 text-right">{(alloc.energy_kwh || 0).toLocaleString()}</td>}
                  <td className="px-4 py-3 text-right">
                    <div>{alloc.energy_share_percent || alloc.mass_share_percent}%</div>
                    {alloc.co_production_group && alloc.group_mass_share_percent != null && (
                      <div className="text-xs text-indigo-600 mt-0.5" title={`${alloc.group_mass_share_percent}% within ${alloc.co_production_group} (${alloc.co_production_method?.replace('_', ' ')})`}>
                        Group: {alloc.group_mass_share_percent}% (mass)
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">{alloc.production_mt?.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right font-medium text-emerald-600">{alloc.allocated_co2_tco2?.toFixed(4)}</td>
                  <td className="px-4 py-3 text-right font-mono text-orange-700">{(alloc.scope1_co2_tco2 || 0).toFixed(4)}</td>
                  <td className="px-4 py-3 text-right font-mono text-indigo-700">{(alloc.scope2_co2_tco2 || 0).toFixed(4)}</td>
                  <td className="px-4 py-3 text-right font-mono">{alloc.intensity_tco2_per_mt?.toFixed(6)}</td>
                  <td className="px-4 py-3 text-right font-mono text-orange-600">€{estCostPerMt.toFixed(2)}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      method === 'process' ? 'bg-green-100 text-green-700' :
                      method === 'energy' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`} title="Confidence reflects data and allocation strength, not regulatory validity">
                      {method === 'process' ? '🟢 High' : method === 'energy' ? '🟡 Medium' : '🔴 Low'}
                    </span>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Cost preview note */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
          <p><strong>Est. CBAM Cost/MT</strong> = Intensity &times; Carbon Price (€{carbonPrice || 80}/tCO₂). This is the <em>gross</em> cost before any carbon price deductions paid in the country of origin. Deductions are applied per shipment.</p>
        </div>
      </>
    )}
  </div>
  );
};

const ShipmentsTab = ({ shipments, products, carbonPrice, onAdd }) => (
  <div>
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-bold text-gray-900">EU Shipments</h3>
      <button
        onClick={onAdd}
        className="px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 flex items-center gap-2"
      >
        <Plus className="w-4 h-4" /> Add Shipment
      </button>
    </div>

    {shipments.length === 0 ? (
      <div className="text-center py-12 bg-gray-50 rounded-lg">
        <Ship className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600">No shipments recorded yet.</p>
        <p className="text-sm text-gray-500">Add EU export shipments to calculate CBAM liability.</p>
      </div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold">Product</th>
              <th className="px-4 py-3 text-left text-sm font-semibold">HS Code</th>
              <th className="px-4 py-3 text-right text-sm font-semibold">Qty (MT)</th>
              <th className="px-4 py-3 text-right text-sm font-semibold">Intensity</th>
              <th className="px-4 py-3 text-right text-sm font-semibold">CO₂ (t)</th>
              <th className="px-4 py-3 text-left text-sm font-semibold">Destination</th>
              <th className="px-4 py-3 text-right text-sm font-semibold">Deduction (€)</th>
              <th className="px-4 py-3 text-right text-sm font-semibold">CBAM Cost (€)</th>
              <th className="px-4 py-3 text-center text-sm font-semibold">Source</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {shipments.map((ship, i) => (
              <tr key={i} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium">{ship.product_name}</td>
                <td className="px-4 py-3 font-mono text-sm">{ship.hs_code}</td>
                <td className="px-4 py-3 text-right">{ship.quantity_mt?.toLocaleString()}</td>
                <td className="px-4 py-3 text-right font-mono text-sm">{ship.intensity_tco2_per_mt?.toFixed(4)}</td>
                <td className="px-4 py-3 text-right font-medium text-emerald-600">{ship.shipment_co2_tco2?.toFixed(2)}</td>
                <td className="px-4 py-3">{ship.destination_country}</td>
                <td className="px-4 py-3 text-right text-green-600">
                  {(ship.carbon_price_paid_origin_eur || 0) > 0 ? `−€${ship.carbon_price_paid_origin_eur?.toLocaleString()}` : '—'}
                </td>
                <td className="px-4 py-3 text-right font-bold text-orange-600">€{ship.cbam_cost_eur?.toLocaleString()}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                    ship.data_source === 'actual' ? 'bg-green-100 text-green-700' :
                    ship.data_source === 'default' ? 'bg-amber-100 text-amber-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {ship.data_source === 'actual' ? 'Actual' : ship.data_source === 'default' ? 'EU Default' : ship.data_source || 'Actual'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-gray-100">
            <tr>
              <td colSpan={2} className="px-4 py-3 font-bold">Total</td>
              <td className="px-4 py-3 text-right font-bold">{shipments.reduce((s, x) => s + (x.quantity_mt || 0), 0).toLocaleString()}</td>
              <td className="px-4 py-3"></td>
              <td className="px-4 py-3 text-right font-bold text-emerald-600">{shipments.reduce((s, x) => s + (x.shipment_co2_tco2 || 0), 0).toFixed(2)}</td>
              <td className="px-4 py-3"></td>
              <td className="px-4 py-3 text-right font-bold text-green-600">−€{shipments.reduce((s, x) => s + (x.carbon_price_paid_origin_eur || 0), 0).toLocaleString()}</td>
              <td className="px-4 py-3 text-right font-bold text-orange-600">€{shipments.reduce((s, x) => s + (x.cbam_cost_eur || 0), 0).toLocaleString()}</td>
              <td className="px-4 py-3"></td>
            </tr>
          </tfoot>
        </table>
      </div>
    )}
  </div>
);

const DashboardTab = ({ dashboard, scenarios = [], settings = {} }) => {
  if (!dashboard) return <div className="text-center py-8">Loading dashboard...</div>;

  const summary = dashboard.summary || {};
  const exchangeRate = settings.exchange_rate_eur || 90;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="bg-blue-50 rounded-lg p-4">
          <p className="text-sm text-blue-600">Total Shipments</p>
          <p className="text-3xl font-bold text-gray-900">{summary.total_shipments || 0}</p>
          <p className="text-xs text-blue-500 mt-1">{summary.total_quantity_mt?.toLocaleString() || 0} MT total</p>
        </div>
        <div className="bg-emerald-50 rounded-lg p-4">
          <p className="text-sm text-emerald-600">Embedded CO₂</p>
          <p className="text-3xl font-bold text-gray-900">{summary.total_embedded_co2_tco2?.toFixed(2) || 0} t</p>
          <p className="text-xs text-emerald-500 mt-1">Avg {summary.avg_intensity_tco2_per_mt?.toFixed(4) || 0} tCO₂/MT</p>
        </div>
        <div className="bg-orange-50 rounded-lg p-4">
          <p className="text-sm text-orange-600">Gross CBAM Cost</p>
          <p className="text-3xl font-bold text-gray-900">€{summary.total_cbam_cost_eur?.toLocaleString() || 0}</p>
          <p className="text-xs text-orange-500 mt-1">@ €{summary.carbon_price_eur}/tCO₂</p>
        </div>
      </div>

      {/* Deduction + Net Liability */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-sm text-green-600">Carbon Price Paid in Origin</p>
          <p className="text-2xl font-bold text-green-700">−€{summary.total_deduction_eur?.toLocaleString() || 0}</p>
          <p className="text-xs text-green-500 mt-1">Deducted from CBAM liability</p>
        </div>
        <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
          <p className="text-sm text-red-600 font-medium">Net CBAM Liability</p>
          <p className="text-3xl font-bold text-red-600">€{summary.net_liability_eur?.toLocaleString() || 0}</p>
          <p className="text-xs text-red-400 mt-1">Gross cost − deductions</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-sm text-gray-600">Data Source Breakdown</p>
          <div className="flex items-center gap-3 mt-2">
            <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">{summary.actual_data_shipments || 0} Actual</span>
            <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs font-medium">{summary.default_data_shipments || 0} EU Default</span>
          </div>
        </div>
      </div>

      {/* By Destination */}
      {Object.keys(dashboard.by_destination || {}).length > 0 && (
        <div>
          <h4 className="font-semibold text-gray-900 mb-3">By Destination</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(dashboard.by_destination).map(([dest, data]) => (
              <div key={dest} className="bg-white border rounded-lg p-4">
                <p className="font-medium text-gray-900 flex items-center gap-2">
                  <Globe className="w-4 h-4 text-blue-500" /> {dest}
                </p>
                <div className="mt-2 text-sm text-gray-600 space-y-1">
                  <p>Quantity: {data.quantity_mt?.toLocaleString()} MT</p>
                  <p>CO₂: {data.co2_tco2?.toFixed(2)} t</p>
                  <p className="font-medium text-orange-600">Cost: €{data.cost_eur?.toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* By Product */}
      {Object.keys(dashboard.by_product || {}).length > 0 && (
        <div>
          <h4 className="font-semibold text-gray-900 mb-3">By Product</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left">Product</th>
                  <th className="px-4 py-2 text-right">Quantity (MT)</th>
                  <th className="px-4 py-2 text-right">CO₂ (t)</th>
                  <th className="px-4 py-2 text-right">CBAM Cost (€)</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {Object.entries(dashboard.by_product).map(([prod, data]) => (
                  <tr key={prod}>
                    <td className="px-4 py-2 font-medium">{prod}</td>
                    <td className="px-4 py-2 text-right">{data.quantity_mt?.toLocaleString()}</td>
                    <td className="px-4 py-2 text-right">{data.co2_tco2?.toFixed(2)}</td>
                    <td className="px-4 py-2 text-right font-medium text-orange-600">€{data.cost_eur?.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Multi-Scenario Cost Exposure */}
      {scenarios.length > 0 && Object.keys(dashboard.by_product || {}).length > 0 && (
        <div>
          <h4 className="font-semibold text-gray-900 mb-3">Cost Exposure by Scenario</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-teal-50">
                <tr>
                  <th className="px-4 py-2 text-left">Product</th>
                  <th className="px-4 py-2 text-right">CO₂ (t)</th>
                  {scenarios.map(s => (
                    <th key={s.id} className="px-4 py-2 text-right">
                      {s.scenario_name}<br/><span className="text-xs font-normal text-gray-500">€{s.price_eur}/t</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y">
                {Object.entries(dashboard.by_product).map(([prod, data]) => (
                  <tr key={prod}>
                    <td className="px-4 py-2 font-medium">{prod}</td>
                    <td className="px-4 py-2 text-right">{data.co2_tco2?.toFixed(2)}</td>
                    {scenarios.map(s => (
                      <td key={s.id} className="px-4 py-2 text-right font-medium text-orange-600">
                        €{(data.co2_tco2 * s.price_eur)?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </td>
                    ))}
                  </tr>
                ))}
                <tr className="bg-gray-50 font-bold">
                  <td className="px-4 py-2">TOTAL</td>
                  <td className="px-4 py-2 text-right">{summary.total_embedded_co2_tco2?.toFixed(2)}</td>
                  {scenarios.map(s => (
                    <td key={s.id} className="px-4 py-2 text-right text-red-600">
                      €{((summary.total_embedded_co2_tco2 || 0) * s.price_eur)?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== MODALS ====================

const ProductFormModal = ({ product, onSave, onClose }) => {
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const [form, setForm] = useState({
    product_name: product?.product_name || '',
    hs_code: product?.hs_code || '',
    process_route: product?.process_route || '',
    unit: 'MT',
    cbam_applicable: product?.cbam_applicable ?? true,
    cn_code: product?.cn_code || '',
    is_complex_good: product?.is_complex_good || false
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.hs_code) {
      showWarning('HS Code is mandatory for CBAM products');
      return;
    }
    onSave(form);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
        <div className="bg-emerald-600 px-6 py-4 rounded-t-xl">
          <h3 className="text-lg font-bold text-white">{product ? 'Edit Product' : 'Add Product'}</h3>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
            <input
              type="text"
              value={form.product_name}
              onChange={(e) => setForm({ ...form, product_name: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="e.g., Hot Rolled Coil (HRC)"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">HS Code * (Mandatory for CBAM)</label>
            <input
              type="text"
              value={form.hs_code}
              onChange={(e) => setForm({ ...form, hs_code: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono"
              placeholder="e.g., 7208.10"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">CN Code (8-digit, optional)</label>
            <input
              type="text"
              value={form.cn_code}
              onChange={(e) => setForm({ ...form, cn_code: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono"
              placeholder="e.g., 72081000"
            />
            <p className="text-xs text-gray-500 mt-1">Combined Nomenclature code — auto-derives CBAM category if provided</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Process Route</label>
            <select
              value={form.process_route}
              onChange={(e) => setForm({ ...form, process_route: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">Select process route</option>
              <option value="BF-BOF">BF-BOF (Blast Furnace)</option>
              <option value="EAF">EAF (Electric Arc Furnace)</option>
              <option value="DRI-EAF">DRI-EAF</option>
              <option value="Casting">Casting</option>
              <option value="Rolling">Rolling</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
            <input
              type="text"
              value="MT (Metric Tonnes)"
              disabled
              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
            />
            <p className="text-xs text-gray-500 mt-1">Unit is locked to MT for CBAM compliance</p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="cbam_applicable"
              checked={form.cbam_applicable}
              onChange={(e) => setForm({ ...form, cbam_applicable: e.target.checked })}
              className="w-4 h-4 text-emerald-600 rounded"
            />
            <label htmlFor="cbam_applicable" className="text-sm text-gray-700">CBAM Applicable</label>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2">
              <Save className="w-4 h-4" /> {product ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ShipmentFormModal = ({ products, carbonPrice, onSave, onClose }) => {
  const [form, setForm] = useState({
    product_id: '',
    quantity_mt: '',
    destination_country: '',
    shipment_date: new Date().toISOString().split('T')[0],
    invoice_number: '',
    carbon_price_paid_origin_eur: ''
  });

  const euCountries = [
    'Austria', 'Belgium', 'Bulgaria', 'Croatia', 'Cyprus', 'Czech Republic',
    'Denmark', 'Estonia', 'Finland', 'France', 'Germany', 'Greece', 'Hungary',
    'Ireland', 'Italy', 'Latvia', 'Lithuania', 'Luxembourg', 'Malta', 'Netherlands',
    'Poland', 'Portugal', 'Romania', 'Slovakia', 'Slovenia', 'Spain', 'Sweden'
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      quantity_mt: parseFloat(form.quantity_mt) || 0,
      carbon_price_paid_origin_eur: parseFloat(form.carbon_price_paid_origin_eur) || 0
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
        <div className="bg-emerald-600 px-6 py-4 rounded-t-xl">
          <h3 className="text-lg font-bold text-white">Add EU Shipment</h3>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Product *</label>
            <select
              value={form.product_id}
              onChange={(e) => setForm({ ...form, product_id: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">Select product</option>
              {products.filter(p => p.cbam_applicable).map(p => (
                <option key={p.id} value={p.id}>{p.product_name} ({p.hs_code})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Quantity (MT) *</label>
            <input
              type="number"
              value={form.quantity_mt}
              onChange={(e) => setForm({ ...form, quantity_mt: e.target.value })}
              required
              step="0.01"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="e.g., 500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Destination Country *</label>
            <select
              value={form.destination_country}
              onChange={(e) => setForm({ ...form, destination_country: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">Select EU country</option>
              {euCountries.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Shipment Date</label>
              <input
                type="date"
                value={form.shipment_date}
                onChange={(e) => setForm({ ...form, shipment_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Number</label>
              <input
                type="text"
                value={form.invoice_number}
                onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="INV-001"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Carbon Price Paid in Origin (EUR)</label>
            <input
              type="number"
              value={form.carbon_price_paid_origin_eur}
              onChange={(e) => setForm({ ...form, carbon_price_paid_origin_eur: e.target.value })}
              step="0.01"
              min="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="0.00 (deducted from CBAM cost)"
            />
            <p className="text-xs text-gray-500 mt-1">Amount already paid for carbon tax/ETS in country of origin — will be deducted from CBAM liability</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
            <p>Carbon Price: <strong>€{carbonPrice}/tCO₂</strong></p>
            <p className="text-xs mt-1">CBAM cost = (embedded CO₂ × carbon price) − carbon price paid in origin.</p>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2">
              <Ship className="w-4 h-4" /> Create Shipment
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ==================== QUARTERLY REPORTS TAB ====================

const QuarterlyReportsTab = ({ reports, dataQuality, studyId, onRefresh }) => {
  const { showSuccess, showError, showWarning } = useAlert();
  const [generating, setGenerating] = useState(false);
  const [selectedQuarter, setSelectedQuarter] = useState('');

  // Generate quarter options for last 2 years
  const quarterOptions = (() => {
    const options = [];
    const now = new Date();
    for (let y = now.getFullYear(); y >= now.getFullYear() - 1; y--) {
      for (let q = 4; q >= 1; q--) {
        options.push(`${y}-Q${q}`);
      }
    }
    return options;
  })();

  const generateReport = async () => {
    if (!selectedQuarter) { showWarning('Please select a quarter'); return; }
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/api/cbam/${studyId}/quarterly-reports/generate?quarter=${selectedQuarter}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess(`Quarterly report for ${selectedQuarter} generated — ${res.data.shipment_count} shipments, quality: ${res.data.data_quality_rating}`);
      onRefresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.errors) {
        const msgs = detail.errors.map(e => e.message).join('\n• ');
        showError(`${detail.message}\n\n• ${msgs}`);
      } else {
        showError(typeof detail === 'string' ? detail : 'Failed to generate quarterly report');
      }
    } finally {
      setGenerating(false);
    }
  };

  const submitReport = async (reportId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/cbam/${studyId}/quarterly-reports/${reportId}/submit`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess('Quarterly report submitted');
      onRefresh();
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to submit report');
    }
  };

  const deleteReport = async (reportId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/cbam/${studyId}/quarterly-reports/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess('Quarterly report deleted');
      onRefresh();
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to delete report');
    }
  };

  const exportXml = async (reportId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API}/api/cbam/${studyId}/quarterly-reports/${reportId}/export/xml`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/xml' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `CBAM_Quarterly_${reportId.slice(0, 8)}.xml`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showError('Failed to export XML');
    }
  };

  const exportXlsx = (reportId) => {
    const token = localStorage.getItem('token');
    window.open(`${API}/api/cbam/${studyId}/quarterly-reports/${reportId}/export/xlsx?token=${token}`, '_blank');
  };

  const qualityColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-50 border-green-200';
    if (score >= 50) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  return (
    <div className="space-y-6">
      {/* Data Quality Card */}
      {dataQuality && (
        <div className={`rounded-lg border p-4 ${qualityColor(dataQuality.quality_score)}`}>
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Data Quality Score
            </h4>
            <span className="text-2xl font-bold">{dataQuality.quality_score}%</span>
          </div>
          <div className="grid grid-cols-3 gap-4 text-sm mt-2">
            <div>Actual data: <strong>{dataQuality.actual_data_count}</strong> shipments</div>
            <div>Default data: <strong>{dataQuality.default_data_count}</strong> shipments</div>
            <div>Products with intensity: <strong>{dataQuality.products_with_intensity}/{dataQuality.products_total}</strong></div>
          </div>
          {dataQuality.issues?.length > 0 && (
            <ul className="mt-2 text-xs space-y-1">
              {dataQuality.issues.map((issue, i) => (
                <li key={i} className="flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> {issue}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Generate Report */}
      <div className="bg-white rounded-lg border p-4">
        <h4 className="font-semibold mb-3 flex items-center gap-2">
          <Globe className="w-4 h-4" /> Generate Quarterly CBAM Report
        </h4>
        <div className="flex items-center gap-3">
          <select
            value={selectedQuarter}
            onChange={(e) => setSelectedQuarter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">Select quarter...</option>
            {quarterOptions.map(q => (
              <option key={q} value={q}>{q}</option>
            ))}
          </select>
          <button
            onClick={generateReport}
            disabled={generating || !selectedQuarter}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
          >
            {generating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {generating ? 'Generating...' : 'Generate Report'}
          </button>
          <button onClick={onRefresh} className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Aggregates all shipments within the selected quarter into an immutable CBAM report.
        </p>
      </div>

      {/* Reports List */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quarter</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Goods (MT)</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Embedded CO₂</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">CBAM Cost</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Deduction</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Net Liability</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Quality</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {reports.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                  No quarterly reports yet. Select a quarter and click Generate.
                </td>
              </tr>
            )}
            {reports.map(r => (
              <tr key={r.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium">{r.reporting_quarter}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    r.status === 'submitted' ? 'bg-blue-100 text-blue-700' :
                    r.status === 'accepted' ? 'bg-green-100 text-green-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {r.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-right font-mono">{r.total_goods_mt?.toFixed(2)}</td>
                <td className="px-4 py-3 text-right font-mono">{r.total_embedded_co2?.toFixed(4)} tCO₂</td>
                <td className="px-4 py-3 text-right font-mono">€{r.total_cbam_cost_eur?.toFixed(2)}</td>
                <td className="px-4 py-3 text-right font-mono text-green-600">−€{r.carbon_price_deduction_eur?.toFixed(2)}</td>
                <td className="px-4 py-3 text-right font-mono font-bold">€{r.net_liability_eur?.toFixed(2)}</td>
                <td className="px-4 py-3 text-right">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${qualityColor(r.data_quality_score)}`}>
                    {r.data_quality_score?.toFixed(0)}%
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => exportXlsx(r.id)}
                      className="text-teal-600 hover:text-teal-800 flex items-center gap-0.5"
                      title="Export Engine-Ready Excel"
                    >
                      <FileSpreadsheet className="w-4 h-4" />
                      <span className="text-[10px] font-semibold">XLSX</span>
                    </button>
                    <button
                      onClick={() => exportXml(r.id)}
                      className="text-blue-600 hover:text-blue-800 flex items-center gap-0.5"
                      title="Export XML"
                    >
                      <FileCode className="w-4 h-4" />
                      <span className="text-[10px] font-semibold">XML</span>
                    </button>
                    {r.status === 'draft' && (
                      <>
                        <button
                          onClick={() => submitReport(r.id)}
                          className="text-emerald-600 hover:text-emerald-800"
                          title="Submit"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteReport(r.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ==================== PHASE 2 COMPONENTS ====================

const BatchCaptureTab = ({ batches, products, studyId, onRefresh }) => {
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    batch_id: '',
    product_id: '',
    planned_qty_mt: '',
    shift: 'Day',
    actual_production_mt: '',
    scrap_reject_mt: '',
    electricity_start_kwh: '',
    electricity_end_kwh: '',
    fuel_type: '',
    fuel_quantity: ''
  });

  const saveBatch = async () => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/api/cbam/${studyId}/batches`, {
        ...form,
        planned_qty_mt: parseFloat(form.planned_qty_mt) || 0,
        actual_production_mt: parseFloat(form.actual_production_mt) || 0,
        scrap_reject_mt: parseFloat(form.scrap_reject_mt) || 0,
        electricity_start_kwh: parseFloat(form.electricity_start_kwh) || null,
        electricity_end_kwh: parseFloat(form.electricity_end_kwh) || null,
        fuel_quantity: parseFloat(form.fuel_quantity) || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      setShowForm(false);
      setForm({ batch_id: '', product_id: '', planned_qty_mt: '', shift: 'Day', actual_production_mt: '', scrap_reject_mt: '', electricity_start_kwh: '', electricity_end_kwh: '', fuel_type: '', fuel_quantity: '' });
      onRefresh();
    } catch (error) {
      showError('Error saving batch: ' + (error.response?.data?.detail || error.message));
    }
  };

  const syncBatch = async (batchId) => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/api/cbam/${studyId}/batches/${batchId}/sync`, {}, { headers: { Authorization: `Bearer ${token}` } });
      onRefresh();
    } catch (error) {
      showError('Error syncing batch');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Mobile Batch Capture</h3>
          <p className="text-sm text-gray-500">Capture production batches with energy data from shop floor</p>
        </div>
        <button onClick={() => setShowForm(true)} className="px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 flex items-center gap-2">
          <Plus className="w-4 h-4" /> New Batch
        </button>
      </div>

      {batches.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <ClipboardList className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No batches captured yet.</p>
          <p className="text-sm text-gray-500">Use this feature to capture batch-level production and energy data.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left">Batch ID</th>
                <th className="px-3 py-2 text-left">Product</th>
                <th className="px-3 py-2 text-right">Planned (MT)</th>
                <th className="px-3 py-2 text-right">Actual (MT)</th>
                <th className="px-3 py-2 text-right">Net Good (MT)</th>
                <th className="px-3 py-2 text-right">Electricity (kWh)</th>
                <th className="px-3 py-2 text-center">Status</th>
                <th className="px-3 py-2 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {batches.map((batch, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-mono font-medium">{batch.batch_id}</td>
                  <td className="px-3 py-2">{batch.product_name || 'N/A'}</td>
                  <td className="px-3 py-2 text-right">{batch.planned_qty_mt}</td>
                  <td className="px-3 py-2 text-right">{batch.actual_production_mt || '-'}</td>
                  <td className="px-3 py-2 text-right font-medium">{batch.net_good_output_mt || '-'}</td>
                  <td className="px-3 py-2 text-right">{batch.batch_electricity_kwh?.toLocaleString() || '-'}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${batch.status === 'synced' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                      {batch.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-center">
                    {batch.status !== 'synced' && (
                      <button onClick={() => syncBatch(batch.id)} className="text-emerald-600 hover:text-emerald-800 text-sm font-medium">
                        Sync
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Batch Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-emerald-600 px-6 py-4 rounded-t-xl">
              <h3 className="text-lg font-bold text-white">New Batch Capture</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Batch ID</label>
                  <input type="text" value={form.batch_id} onChange={(e) => setForm({ ...form, batch_id: e.target.value })} placeholder="Auto-generated if empty" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Shift</label>
                  <select value={form.shift} onChange={(e) => setForm({ ...form, shift: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                    <option value="Day">Day Shift</option>
                    <option value="Night">Night Shift</option>
                    <option value="General">General Shift</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product *</label>
                <select value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })} required className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                  <option value="">Select product</option>
                  {products.filter(p => p.cbam_applicable).map(p => (
                    <option key={p.id} value={p.id}>{p.product_name}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Planned (MT)</label>
                  <input type="number" value={form.planned_qty_mt} onChange={(e) => setForm({ ...form, planned_qty_mt: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Actual (MT)</label>
                  <input type="number" value={form.actual_production_mt} onChange={(e) => setForm({ ...form, actual_production_mt: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Scrap (MT)</label>
                  <input type="number" value={form.scrap_reject_mt} onChange={(e) => setForm({ ...form, scrap_reject_mt: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
              </div>
              <div className="border-t pt-4">
                <h4 className="font-medium text-gray-900 mb-3">Energy Capture</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Meter Start (kWh)</label>
                    <input type="number" value={form.electricity_start_kwh} onChange={(e) => setForm({ ...form, electricity_start_kwh: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Meter End (kWh)</label>
                    <input type="number" value={form.electricity_end_kwh} onChange={(e) => setForm({ ...form, electricity_end_kwh: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button onClick={() => setShowForm(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                <button onClick={saveBatch} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2">
                  <Save className="w-4 h-4" /> Save Batch
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const EvidenceTab = ({ evidence, renewableOffset, studyId, onRefresh }) => {
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    evidence_type: 'renewable_certificate',
    title: '',
    description: '',
    energy_kwh: '',
    certificate_id: '',
    issuer: '',
    valid_from: '',
    valid_to: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const saveEvidence = async () => {
    const token = localStorage.getItem('token');
    setUploading(true);
    try {
      let document_url = '';
      let document_name = '';

      // Upload file if selected
      if (selectedFile) {
        const formData = new FormData();
        formData.append('file', selectedFile);

        const uploadRes = await axios.post(`${API}/api/cbam/${studyId}/evidence/upload`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        document_url = uploadRes.data.file_url;
        document_name = uploadRes.data.file_name;
      }

      // Save evidence metadata
      await axios.post(`${API}/api/cbam/${studyId}/evidence`, {
        ...form,
        energy_kwh: parseFloat(form.energy_kwh) || 0,
        document_url,
        document_name
      }, { headers: { Authorization: `Bearer ${token}` } });

      setShowForm(false);
      setForm({ evidence_type: 'renewable_certificate', title: '', description: '', energy_kwh: '', certificate_id: '', issuer: '', valid_from: '', valid_to: '' });
      setSelectedFile(null);
      onRefresh();
    } catch (error) {
      showError('Error saving evidence: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };

  const verifyEvidence = async (evidenceId, verified) => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(`${API}/api/cbam/${studyId}/evidence/${evidenceId}/verify?verified=${verified}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      onRefresh();
    } catch (error) {
      showError('Error verifying evidence');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Evidence Locker</h3>
          <p className="text-sm text-gray-500">Renewable certificates and audit documents for CBAM compliance</p>
        </div>
        <button onClick={() => setShowForm(true)} className="px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add Evidence
        </button>
      </div>

      {/* Renewable Offset Summary */}
      {renewableOffset && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h4 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
            <Leaf className="w-5 h-5" /> Renewable Energy Offset Summary
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-green-600">Total Renewable</p>
              <p className="text-xl font-bold text-green-900">{renewableOffset.total_renewable_kwh?.toLocaleString()} kWh</p>
            </div>
            <div>
              <p className="text-sm text-green-600">Verified Renewable</p>
              <p className="text-xl font-bold text-green-900">{renewableOffset.verified_renewable_kwh?.toLocaleString()} kWh</p>
            </div>
            <div>
              <p className="text-sm text-green-600">Potential Offset</p>
              <p className="text-xl font-bold text-green-900">{renewableOffset.potential_offset_tco2?.toFixed(2)} tCO₂</p>
            </div>
            <div>
              <p className="text-sm text-green-600">Verified Offset</p>
              <p className="text-xl font-bold text-green-900">{renewableOffset.verified_offset_tco2?.toFixed(2)} tCO₂</p>
            </div>
          </div>
        </div>
      )}

      {/* Evidence List */}
      {evidence.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No evidence documents uploaded yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {evidence.map((e, i) => (
            <div key={i} className={`rounded-lg p-4 border ${e.verified ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'}`}>
              <div className="flex items-start justify-between">
                <div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${e.evidence_type === 'renewable_certificate' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                    {e.evidence_type?.replace('_', ' ')}
                  </span>
                  <h4 className="font-semibold text-gray-900 mt-2">{e.title}</h4>
                  <p className="text-sm text-gray-600 mt-1">{e.description}</p>
                </div>
                {e.verified ? (
                  <span className="flex items-center gap-1 text-green-600 text-sm"><CheckCircle className="w-4 h-4" /> Verified</span>
                ) : (
                  <button onClick={() => verifyEvidence(e.id, true)} className="text-emerald-600 hover:text-emerald-800 text-sm font-medium">Verify</button>
                )}
              </div>
              {e.energy_kwh > 0 && (
                <p className="mt-2 text-sm text-gray-600">Energy: <strong>{e.energy_kwh?.toLocaleString()} kWh</strong></p>
              )}
              {e.certificate_id && (
                <p className="text-sm text-gray-500">Certificate: {e.certificate_id}</p>
              )}
              {e.document_url && (
                <a 
                  href={`${API}${e.document_url}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 hover:underline"
                >
                  <Download className="w-3 h-3" /> Download Document
                </a>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Evidence Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
            <div className="bg-emerald-600 px-6 py-4 rounded-t-xl">
              <h3 className="text-lg font-bold text-white">Add Evidence Document</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Evidence Type</label>
                <select value={form.evidence_type} onChange={(e) => setForm({ ...form, evidence_type: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                  <option value="renewable_certificate">Renewable Energy Certificate</option>
                  <option value="meter_reading">Meter Reading</option>
                  <option value="audit_report">Audit Report</option>
                  <option value="other">Other Document</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g., Solar REC Q1 2025" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Upload Document</label>
                <input 
                  type="file" 
                  onChange={handleFileChange}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
                />
                {selectedFile && (
                  <p className="text-sm text-gray-600 mt-1">Selected: {selectedFile.name}</p>
                )}
              </div>
              {form.evidence_type === 'renewable_certificate' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Energy (kWh)</label>
                      <input type="number" value={form.energy_kwh} onChange={(e) => setForm({ ...form, energy_kwh: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Certificate ID</label>
                      <input type="text" value={form.certificate_id} onChange={(e) => setForm({ ...form, certificate_id: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Issuer</label>
                    <input type="text" value={form.issuer} onChange={(e) => setForm({ ...form, issuer: e.target.value })} placeholder="e.g., IEX Green Markets" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                  </div>
                </>
              )}
              <div className="flex justify-end gap-3 pt-4">
                <button onClick={() => { setShowForm(false); setSelectedFile(null); }} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                <button 
                  onClick={saveEvidence} 
                  disabled={uploading}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="w-4 h-4" /> {uploading ? 'Uploading...' : 'Save Evidence'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const WorkflowTab = ({ workflow, currencyInfo, studyId, onRefresh }) => {
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();

  const submitForValidation = async () => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/api/cbam/${studyId}/workflow/submit-for-validation`, {}, { headers: { Authorization: `Bearer ${token}` } });
      onRefresh();
    } catch (error) {
      showError('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const validate = async (approved) => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/api/cbam/${studyId}/workflow/validate?approved=${approved}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      onRefresh();
    } catch (error) {
      showError('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const approve = async (approved) => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/api/cbam/${studyId}/workflow/approve?approved=${approved}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      onRefresh();
    } catch (error) {
      showError('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const exportCSV = () => {
    const token = localStorage.getItem('token');
    window.open(`${API}/api/cbam/${studyId}/export/csv?token=${token}`, '_blank');
  };

  const exportXML = () => {
    const token = localStorage.getItem('token');
    window.open(`${API}/api/cbam/${studyId}/export/xml?token=${token}`, '_blank');
  };

  const exportPDFReport = () => {
    const token = localStorage.getItem('token');
    window.open(`${API}/api/cbam/${studyId}/export/pdf-report?token=${token}`, '_blank');
  };

  const exportEngineReadyXlsx = () => {
    const token = localStorage.getItem('token');
    window.open(`${API}/api/cbam/${studyId}/export/engine-ready-xlsx?token=${token}`, '_blank');
  };

  const stages = workflow?.stages || {};
  const status = workflow?.status || 'draft';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Maker-Checker Approval Workflow</h3>
          <p className="text-sm text-gray-500">Energy Analyst → ESG Analyst → CFO</p>
        </div>
        {workflow?.can_generate_report && (
          <div className="flex gap-2">
            <button onClick={exportEngineReadyXlsx} className="px-4 py-2 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 flex items-center gap-2">
              <Download className="w-4 h-4" /> Engine-Ready Excel
            </button>
            <button onClick={exportPDFReport} className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 flex items-center gap-2">
              <FileText className="w-4 h-4" /> CBAM Report (PDF)
            </button>
            <button onClick={exportCSV} className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 flex items-center gap-2">
              <Download className="w-4 h-4" /> Export CSV
            </button>
            <button onClick={exportXML} className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 flex items-center gap-2">
              <Download className="w-4 h-4" /> Export XML
            </button>
          </div>
        )}
      </div>

      {/* Currency Info */}
      {currencyInfo && !currencyInfo.is_eu_country && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
            <Globe className="w-4 h-4" /> Currency Conversion
          </h4>
          <p className="text-sm text-blue-800">
            Site location: <strong>{currencyInfo.country}</strong> (Non-EU)
          </p>
          <p className="text-sm text-blue-800">
            Local currency: <strong>{currencyInfo.local_currency}</strong> → EUR @ {currencyInfo.exchange_rate_to_eur}
          </p>
        </div>
      )}

      {/* Workflow Steps */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Step 1: Data Entry */}
        <div className={`rounded-xl p-6 border-2 ${stages.data_entry?.status === 'completed' ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white'}`}>
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${stages.data_entry?.status === 'completed' ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
              {stages.data_entry?.status === 'completed' ? <Check className="w-5 h-5" /> : <span>1</span>}
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Data Entry</h4>
              <p className="text-xs text-gray-500">Energy Analyst</p>
            </div>
          </div>
          {stages.data_entry?.status === 'completed' ? (
            <div className="text-sm text-green-700">
              <p>Submitted by: {stages.data_entry.user}</p>
              <p className="text-xs">{stages.data_entry.timestamp}</p>
            </div>
          ) : (
            <button onClick={submitForValidation} className="w-full px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700">
              Submit for Validation
            </button>
          )}
        </div>

        {/* Step 2: Validation */}
        <div className={`rounded-xl p-6 border-2 ${stages.validation?.status === 'approved' ? 'border-green-500 bg-green-50' : stages.validation?.status === 'rejected' ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-white'}`}>
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${stages.validation?.status === 'approved' ? 'bg-green-500 text-white' : stages.validation?.status === 'rejected' ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
              {stages.validation?.status === 'approved' ? <Check className="w-5 h-5" /> : stages.validation?.status === 'rejected' ? <X className="w-5 h-5" /> : <span>2</span>}
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Validation</h4>
              <p className="text-xs text-gray-500">ESG Analyst</p>
            </div>
          </div>
          {stages.validation?.status === 'approved' || stages.validation?.status === 'rejected' ? (
            <div className={`text-sm ${stages.validation.status === 'approved' ? 'text-green-700' : 'text-red-700'}`}>
              <p>{stages.validation.status === 'approved' ? 'Approved' : 'Rejected'} by: {stages.validation.user}</p>
            </div>
          ) : status === 'pending_validation' ? (
            <div className="flex gap-2">
              <button onClick={() => validate(true)} className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700">Approve</button>
              <button onClick={() => validate(false)} className="flex-1 px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">Reject</button>
            </div>
          ) : (
            <p className="text-sm text-gray-400">Waiting for data entry</p>
          )}
        </div>

        {/* Step 3: Final Approval */}
        <div className={`rounded-xl p-6 border-2 ${stages.approval?.status === 'approved' ? 'border-green-500 bg-green-50' : stages.approval?.status === 'rejected' ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-white'}`}>
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${stages.approval?.status === 'approved' ? 'bg-green-500 text-white' : stages.approval?.status === 'rejected' ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
              {stages.approval?.status === 'approved' ? <Check className="w-5 h-5" /> : stages.approval?.status === 'rejected' ? <X className="w-5 h-5" /> : <span>3</span>}
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Final Approval</h4>
              <p className="text-xs text-gray-500">CFO</p>
            </div>
          </div>
          {stages.approval?.status === 'approved' || stages.approval?.status === 'rejected' ? (
            <div className={`text-sm ${stages.approval.status === 'approved' ? 'text-green-700' : 'text-red-700'}`}>
              <p>{stages.approval.status === 'approved' ? 'Approved' : 'Rejected'} by: {stages.approval.user}</p>
            </div>
          ) : status === 'pending_approval' ? (
            <div className="flex gap-2">
              <button onClick={() => approve(true)} className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700">Approve</button>
              <button onClick={() => approve(false)} className="flex-1 px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">Reject</button>
            </div>
          ) : (
            <p className="text-sm text-gray-400">Waiting for validation</p>
          )}
        </div>
      </div>

      {/* Status Banner */}
      {workflow?.can_generate_report && (
        <div className="bg-green-100 border border-green-300 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-green-600" />
            <div>
              <p className="font-semibold text-green-900">CBAM Report Approved</p>
              <p className="text-sm text-green-700">Ready for export and submission to EU authorities</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AuditLogTab = ({ logs, summary, filters, onFilterChange, onRefresh }) => {
  const [viewMode, setViewMode] = useState('logs'); // 'logs' or 'summary'

  // Humanize snake_case event names
  const formatEvent = (event) => {
    if (!event) return '';
    return event
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  // Map events to icons and colors
  const getEventStyle = (event) => {
    const e = (event || '').toLowerCase();
    if (e.includes('created') || e.includes('generated') || e.includes('added'))
      return { color: 'text-green-700 bg-green-50 border-green-200', icon: '+'  };
    if (e.includes('updated') || e.includes('modified') || e.includes('saved'))
      return { color: 'text-blue-700 bg-blue-50 border-blue-200', icon: '~' };
    if (e.includes('deleted') || e.includes('removed'))
      return { color: 'text-red-700 bg-red-50 border-red-200', icon: '-' };
    if (e.includes('locked') || e.includes('submitted'))
      return { color: 'text-purple-700 bg-purple-50 border-purple-200', icon: '\u2713' };
    if (e.includes('approved') || e.includes('validated'))
      return { color: 'text-emerald-700 bg-emerald-50 border-emerald-200', icon: '\u2713' };
    if (e.includes('rejected'))
      return { color: 'text-orange-700 bg-orange-50 border-orange-200', icon: '\u2717' };
    return { color: 'text-gray-700 bg-gray-50 border-gray-200', icon: '\u2022' };
  };

  // Format entity type as a readable label
  const formatEntityType = (type) => {
    if (!type) return '';
    return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  // Extract a meaningful detail line from new_value/changes
  const getDetailLine = (log) => {
    const nv = log.new_value || log.changes || {};
    const parts = [];
    if (nv.quarter) parts.push(`Quarter: ${nv.quarter}`);
    if (nv.shipments) parts.push(`${nv.shipments} shipment(s)`);
    if (nv.product_name) parts.push(nv.product_name);
    if (nv.hs_code) parts.push(`HS ${nv.hs_code}`);
    if (nv.destination_country) parts.push(`\u2192 ${nv.destination_country}`);
    if (nv.quantity_mt) parts.push(`${nv.quantity_mt} MT`);
    if (nv.method) parts.push(`Method: ${nv.method}`);
    return parts.join(' \u00b7 ');
  };

  // Shorten UUID to first 8 chars
  const shortId = (id) => id ? id.substring(0, 8) : '';

  const formatTimestamp = (timestamp) => {
    return formatDateTime(timestamp);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-gray-900">CBAM Audit Log</h3>
          <p className="text-sm text-gray-500">Track all changes for auditor verification</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('logs')}
            className={`px-4 py-2 rounded-lg font-medium ${viewMode === 'logs' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            <Database className="w-4 h-4 inline mr-2" />
            Logs
          </button>
          <button
            onClick={() => setViewMode('summary')}
            className={`px-4 py-2 rounded-lg font-medium ${viewMode === 'summary' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            <BarChart3 className="w-4 h-4 inline mr-2" />
            Summary
          </button>
          <button onClick={onRefresh} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Summary View */}
      {viewMode === 'summary' && summary && (
        <div className="space-y-6">
          {/* Overall Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-1">Total Events</h4>
              <p className="text-3xl font-bold text-blue-700">{summary.total_events || 0}</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-green-900 mb-1">Active Users</h4>
              <p className="text-3xl font-bold text-green-700">{summary.by_user?.length || 0}</p>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-purple-900 mb-1">Entity Types</h4>
              <p className="text-3xl font-bold text-purple-700">{summary.by_entity_type?.length || 0}</p>
            </div>
          </div>

          {/* By Entity Type */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h4 className="font-semibold text-gray-900 mb-4">Events by Entity Type</h4>
            <div className="space-y-3">
              {summary.by_entity_type?.map((item, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">{formatEntityType(item.entity_type)}</span>
                  <div className="flex items-center gap-3">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${(item.count / summary.total_events) * 100}%` }}
                      />
                    </div>
                    <span className="text-sm font-bold text-gray-900 w-12 text-right">{item.count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* By User */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h4 className="font-semibold text-gray-900 mb-4">Activity by User</h4>
            <div className="space-y-3">
              {summary.by_user?.slice(0, 10).map((item, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">{item.user}</span>
                  <div className="flex items-center gap-3">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-600 h-2 rounded-full"
                        style={{ width: `${(item.count / summary.total_events) * 100}%` }}
                      />
                    </div>
                    <span className="text-sm font-bold text-gray-900 w-12 text-right">{item.count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h4 className="font-semibold text-gray-900 mb-4">Recent Activity (Last 10)</h4>
            <div className="space-y-2">
              {summary.recent_activity?.map((log, i) => {
                const style = getEventStyle(log.event);
                return (
                <div key={i} className={`flex items-start gap-3 p-3 rounded-lg ${style.color}`}>
                  <span className="w-5 h-5 rounded-full bg-white/60 flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
                    {style.icon}
                  </span>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{formatEvent(log.event)}</p>
                    <p className="text-xs opacity-70">{log.user_email || log.user || 'System'} &middot; {formatTimestamp(log.timestamp)}</p>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Logs View */}
      {viewMode === 'logs' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Entity Type</label>
                <select
                  value={filters.entity_type}
                  onChange={(e) => onFilterChange({ ...filters, entity_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">All Types</option>
                  <option value="product">Product</option>
                  <option value="production">Production</option>
                  <option value="allocation">Allocation</option>
                  <option value="shipment">Shipment</option>
                  <option value="quarterly_report">Quarterly Report</option>
                  <option value="installation">Installation</option>
                  <option value="precursor">Precursor</option>
                  <option value="workflow">Workflow</option>
                  <option value="evidence">Evidence</option>
                  <option value="batch">Batch</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">User</label>
                <input
                  type="text"
                  value={filters.user_email}
                  onChange={(e) => onFilterChange({ ...filters, user_email: e.target.value })}
                  placeholder="Filter by email..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
          </div>

          {/* Logs List */}
          {logs && logs.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Database className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No audit logs found</p>
            </div>
          ) : (
            <div className="space-y-2">
              {logs && logs.map((log, i) => {
                const style = getEventStyle(log.event);
                const detail = getDetailLine(log);
                return (
                  <div key={i} className={`rounded-lg border p-4 hover:shadow-md transition-shadow ${style.color}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <span className="w-6 h-6 rounded-full bg-white/60 flex items-center justify-center text-xs font-bold">
                            {style.icon}
                          </span>
                          <h4 className="font-semibold">{formatEvent(log.event)}</h4>
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-white/50">
                            {formatEntityType(log.entity_type)}
                          </span>
                        </div>
                        {detail && (
                          <p className="text-sm ml-9 mb-1 opacity-80">{detail}</p>
                        )}
                        <div className="flex items-center gap-4 ml-9 text-xs opacity-70">
                          <span>{log.user_email || 'System'}</span>
                          <span>{formatTimestamp(log.timestamp)}</span>
                          {log.entity_id && <span className="font-mono">#{shortId(log.entity_id)}</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== MULTI-PRODUCT ENHANCEMENT COMPONENTS ====================

const ValidationBanner = ({ issues }) => {
  if (!issues || issues.length === 0) return null;
  const errors = issues.filter(i => i.severity === 'error');
  const warnings = issues.filter(i => i.severity === 'warning');
  return (
    <div className="space-y-2 mb-4">
      {errors.map((issue, i) => (
        <div key={`e-${i}`} className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>{issue.message}</span>
          <span className="ml-auto text-xs text-red-500 font-mono">{issue.code}</span>
        </div>
      ))}
      {warnings.map((issue, i) => (
        <div key={`w-${i}`} className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>{issue.message}</span>
          <span className="ml-auto text-xs text-amber-500 font-mono">{issue.code}</span>
        </div>
      ))}
    </div>
  );
};

const CoProductionGroupFormModal = ({ products, onSave, onClose }) => {
  const [form, setForm] = useState({ group_name: '', allocation_method: 'mass_based', product_ids: [] });
  const availableProducts = products.filter(p => p.cbam_applicable && !p.co_production_group_id);
  const toggleProduct = (pid) => {
    setForm(prev => ({
      ...prev,
      product_ids: prev.product_ids.includes(pid) ? prev.product_ids.filter(id => id !== pid) : [...prev.product_ids, pid],
    }));
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">Create Co-Production Group</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Group Name</label>
            <input value={form.group_name} onChange={e => setForm({...form, group_name: e.target.value})}
              placeholder="e.g., Blast Furnace Products" className="w-full px-3 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Allocation Method</label>
            <select value={form.allocation_method} onChange={e => setForm({...form, allocation_method: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
              <option value="mass_based">Mass-Based</option>
              <option value="value_based">Economic Value-Based</option>
              <option value="physical_causality">Physical Causality</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Products (min 2) - {form.product_ids.length} selected</label>
            <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
              {availableProducts.map(p => (
                <label key={p.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                  <input type="checkbox" checked={form.product_ids.includes(p.id)} onChange={() => toggleProduct(p.id)} className="rounded" />
                  <span className="text-sm text-gray-800">{p.product_name}</span>
                  <span className="text-xs text-gray-500 font-mono ml-auto">{p.hs_code}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
            <button onClick={() => onSave(form)} disabled={form.product_ids.length < 2 || !form.group_name}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">Create Group</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CBAMModule;
